import React from 'react'
import { GET_INTEGRATIONS } from 'components/Integrations/queries'
import { FetchedIntegration } from 'components/Integrations/types'
import { useIntegration } from 'hooks/useGlobalContext/useIntegration'
import { useMessageStore } from 'hooks/useGlobalContext/useMessageStore'
import { LoaderProps } from './types'
import { useFetchOnce } from './useFetchOnce'

// Per-partner connection status (GetIntegrations), read by ManageLeat to
// decide its own routing — shared GraphQL data, so Loader/Context per
// doc/ARCHITECTURE.md §6. A failure here can only be toasted, which is why
// the Integrations listing page keeps its own fetch.
export const IntegrationsLoader = ({
  isParentLoaded = true,
  children
}: LoaderProps) => {
  const { integrations, setIntegrations, setRefetchIntegrations } =
    useIntegration()

  const { addMessage } = useMessageStore()

  const { isLoaded } = useFetchOnce<{
    getIntegrations: FetchedIntegration[]
  }>({
    query: GET_INTEGRATIONS,
    skip: integrations !== null,
    // isLoaded flips true either way, so without this the app renders on a
    // null Context with nothing said.
    onError: () => {
      addMessage({ type: 'error', content: 'Failed to load integrations' })
    },
    onData: (data) => {
      if (data.getIntegrations) {
        setIntegrations(data.getIntegrations)
      }
    },
    onRefetch: (refetch) => {
      const refetchAndSync = async () => {
        const result = (await refetch()) as {
          data?: { getIntegrations: FetchedIntegration[] }
        }
        if (result.data?.getIntegrations) {
          setIntegrations(result.data.getIntegrations)
        }
        return result
      }

      setRefetchIntegrations(
        (() => refetchAndSync) as unknown as () => Promise<unknown>
      )
    },
    isParentLoaded
  })

  return <React.Fragment>{children(isLoaded)}</React.Fragment>
}
